// @ts-nocheck
import { useState } from 'react';
import { ownerApi } from './ownerApi';
import { businessDisplayName, scheduledPlanChangeSentence } from './lib/format';
import { label } from './lib/labels';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import Alert from './ui/Alert';

const PLANS = ['lite', 'starter', 'growth', 'pro', 'max'];
const PLAN_RANK = { lite: 0, starter: 1, growth: 2, pro: 3, max: 4 };

/** @param {string | undefined} current @param {string} target */
function isDowngrade(current, target) {
  const from = PLAN_RANK[current || ''];
  const to = PLAN_RANK[target];
  if (from == null || to == null) return false;
  return to < from;
}

/** @param {{ businesses: any[], onGranted?: (message: string) => void }} props */
export default function PlanAssignment({ businesses, onGranted }) {
  const [tenantId, setTenantId] = useState('');
  const [planId, setPlanId] = useState('lite');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const rows = businesses || [];
  const selected = rows.find((row) => row.tenant_id === tenantId);
  const business = selected ? businessDisplayName(selected) : 'this business';
  const planName = label('plan', planId);
  const currentName = label('plan', selected?.subscription || 'none');
  const scheduling = isDowngrade(selected?.subscription, planId);
  const pending = selected?.pending_downgrade;

  const grant = async () => {
    setError('');
    try {
      const result = await ownerApi.assignPlan({ tenant_id: tenantId, plan_id: planId });
      setConfirming(false);
      if (!onGranted) return;
      if (result.scheduled_downgrade) {
        const sentence = scheduledPlanChangeSentence(planName, result.pending_downgrade?.effective_at);
        const stays = label('plan', result.plan_id || selected?.subscription || 'none');
        onGranted(`${business} stays on ${stays}. ${sentence} Recorded in the activity log.`);
        return;
      }
      const messages = Number(result.included_messages || 0).toLocaleString('en-US');
      onGranted(`${business} is on ${planName}. Included messages: ${messages}. Recorded in the activity log.`);
    } catch (reason) {
      setConfirming(false);
      setError(reason instanceof Error ? reason.message : 'The plan was not granted.');
    }
  };

  const cancelDowngrade = async () => {
    if (!tenantId) return;
    setError('');
    try {
      await ownerApi.cancelScheduledDowngrade({ tenant_id: tenantId });
      if (onGranted) onGranted(`Scheduled downgrade cancelled. ${business} stays on ${currentName}.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The scheduled downgrade was not cancelled.');
    }
  };

  return (
    <section className="rounded-xl border border-[#E2E8F0] bg-white p-4">
      <h2 className="text-base font-semibold text-slate-900">Assign a plan</h2>
      <p className="mt-1 text-sm text-slate-600">
        Pick a business and a plan, then grant it. An upgrade applies now and adds the message allowance for that plan. A downgrade waits until the current period ends. No card is charged.
      </p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Business</span>
          <select
            aria-label="Business"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            className="h-9 min-w-56 rounded-lg border border-[#7C8798] bg-white px-3 text-sm"
          >
            <option value="">Choose a business</option>
            {rows.map((row) => (
              <option key={row.tenant_id} value={row.tenant_id}>{businessDisplayName(row)}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Plan</span>
          <select
            aria-label="Plan"
            value={planId}
            onChange={(event) => setPlanId(event.target.value)}
            className="h-9 rounded-lg border border-[#7C8798] bg-white px-3 text-sm"
          >
            {PLANS.map((id) => (
              <option key={id} value={id}>{label('plan', id)}</option>
            ))}
          </select>
        </label>
        <Button disabled={!tenantId} onClick={() => setConfirming(true)}>Grant plan</Button>
      </div>
      {pending ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="text-sm text-slate-800">
            {scheduledPlanChangeSentence(label('plan', pending.plan_id), pending.effective_at)}
          </p>
          <Button variant="secondary" onClick={cancelDowngrade}>Cancel downgrade</Button>
        </div>
      ) : null}
      {error ? <div className="mt-3"><Alert title="The plan was not granted." detail={error} /></div> : null}
      {confirming ? (
        <ConfirmDialog
          title={scheduling ? `Schedule ${planName} for ${business}?` : `Grant ${planName} to ${business}?`}
          body={
            scheduling
              ? `${business} keeps ${currentName} and its message allowance until the current period ends. The plan then changes to ${planName} and that allowance is granted. No payment is taken. This is written to the activity log.`
              : 'The upgrade applies now and adds the message allowance for that plan. No payment is taken. This is written to the activity log.'
          }
          confirmLabel={scheduling ? 'Schedule downgrade' : 'Grant plan'}
          variant="primary"
          loadingLabel={scheduling ? 'Scheduling…' : 'Granting…'}
          onClose={() => setConfirming(false)}
          onConfirm={grant}
        />
      ) : null}
    </section>
  );
}
